import React, { useState } from "react";

const CustomDateRangePicker = ({ setRange, range, setIshowCalendar }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const daysOfWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const formatDate = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    return days;
  };

  const handleSelectDate = (date) => {
    if (!range.start || (range.start && range.end)) {
      setRange({ start: formatDate(date), end: null });
    } else if (new Date(date) < new Date(range.start)) {
      setRange({ start: formatDate(date), end: range.start });
      setIshowCalendar(false);
    } else {
      setRange({ ...range, end: formatDate(date) });
      setIshowCalendar(false);
    }
  };

  const isSameDay = (d1, d2) =>
    d1 && d2 && new Date(d1).toDateString() === new Date(d2).toDateString();

  const isInRange = (day) => {
    if (!range.start || !range.end) return false;
    return day >= new Date(range.start) && day <= new Date(range.end);
  };

  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const renderCalendar = (monthOffset = 0) => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + monthOffset
    );
    const days = getDaysInMonth(date);

    return (
      <div className="w-full lg:w-[200px]">
        {/* Month header */}
        <div className="flex justify-center items-center mb-2">
          <h2 className="font-semibold text-lg">
            {date.toLocaleString("default", { month: "long" })}{" "}
            {date.getFullYear()}
          </h2>
        </div>

        {/* Days of week */}
        <div className="grid grid-cols-7 text-center text-sm font-medium text-gray-500 mb-1">
          {daysOfWeek.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>

        {/* Dates grid */}
        <div className="grid grid-cols-7 gap-1 text-center text-sm">
          {days.map((day, i) => {
            const isSelectedStart = isSameDay(day, range.start);
            const isSelectedEnd = isSameDay(day, range.end);
            const inRange = isInRange(day);

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isPast = day && day < today;

            return (
              <div
                key={i}
                onClick={() => day && !isPast && handleSelectDate(day)}
                className={`cursor-pointer py-2 rounded-lg transition-all
                  ${
                    !day
                      ? ""
                      : isPast
                      ? "text-gray-300 cursor-not-allowed "
                      : "hover:bg-blue-100"
                  }
                  ${inRange ? "bg-blue-200" : ""}
                  ${
                    isSelectedStart || isSelectedEnd
                      ? "bg-gradient-to-tl from-blue-900 to-sky-500 text-white"
                      : ""
                  }
                `}
              >
                {day ? day.getDate() : ""}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="inline-block p-4  bg-white shadow-lg rounded-xl  drop-shadow-md">
      {/* Navigation */}
      <div className="flex justify-between mb-3 items-center gap-8 lg:gap-0">
        <button
          onClick={prevMonth}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          ←
        </button>
        <span className="text-sm text-nowrap font-medium text-gray-700">
          Select a date range
        </span>
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          →
        </button>
      </div>

      {/* Two Calendars */}
      <div className="flex lg:flex-row flex-col gap-6">
        {renderCalendar(0)}
        {renderCalendar(1)}
      </div>
    </div>
  );
};

export default CustomDateRangePicker;
